import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Card, CardContent, TextField, Chip, Avatar, Alert } from '@mui/material';
import { Wallet, TrendingUp, TrendingDown, Clock, AlertCircle, CheckCircle2, CheckCircle, UserMinus, PieChart, Hourglass, Award, BarChart3, Plus, FileText, Layers, Activity, Receipt, CreditCard, Users, ArrowUpRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, AreaChart, Area, Legend } from 'recharts';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Loader } from '@/components/feedback/Loader';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import { ROLES } from '@/constants/roles';
import { feeService } from '@/services/feeService';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { useNavigate } from 'react-router-dom';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';

const PAYMENT_MODE_COLORS = {
  UPI: '#2563EB',
  CASH: '#16A34A',
  CHEQUE: '#F59E0B',
  BANK_TRANSFER: '#8B5CF6',
  CARD: '#EC4899',
  OTHER: '#06B6D4',
};
const FALLBACK_COLORS = ['#2563EB', '#16A34A', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];
const CHART_COLOR = '#2563EB';

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length < 3) return dateStr;
  const [year, month, day] = parts;
  const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateToISO = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DATE_PRESET_OPTIONS = [
  { value: 'thisMonth', label: 'This Month' },
  { value: 'today', label: 'Today' },
  { value: '7days', label: '7 Days' },
  { value: '15days', label: '15 Days' },
  { value: '1month', label: 'One Month' },
  { value: '2months', label: '2 Months' },
  { value: 'custom', label: 'Custom' },
];

const getPresetDates = (preset) => {
  const now = new Date();
  const todayStr = formatDateToISO(now);

  switch (preset) {
    case 'today':
      return { from: todayStr, to: todayStr };
    case '7days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '15days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 14);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case 'thisMonth': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '1month': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      return { from: formatDateToISO(d), to: todayStr };
    }
    case '2months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 2);
      return { from: formatDateToISO(d), to: todayStr };
    }
    default:
      return null;
  }
};

// Unified stat card with consistent design tokens
function DashboardStatCard({ title, value, subtext, subtextColor, icon: Icon, color = 'primary', onClick }) {
  const colorMap = {
    primary: tokens.colors.primary.main,
    success: tokens.colors.success.main,
    warning: tokens.colors.warning.main,
    error: tokens.colors.error.main,
    info: tokens.colors.info.main,
    secondary: '#EC4899',
    purple: '#8B5CF6',
  };
  const themeColor = colorMap[color] || (typeof color === 'string' && color.startsWith('#') ? color : tokens.colors.primary.main);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        minHeight: 112,
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        border: `1px solid ${tokens.colors.border}`,
        boxShadow: tokens.shadows.card,
        '&:hover': {
          boxShadow: tokens.shadows.md,
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: tokens.colors.text.muted,
                mb: 0.5,
                fontWeight: 500,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: tokens.colors.text.primary,
                lineHeight: 1.2,
                fontSize: '1.45rem',
                mb: 0.5,
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: subtextColor || tokens.colors.text.muted,
                fontWeight: subtextColor ? 600 : 500,
                fontSize: '0.75rem',
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {subtext || '\u00A0'}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: tokens.radius.md,
              backgroundColor: `${themeColor}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ml: 1.5,
            }}
          >
            <Icon size={22} color={themeColor} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Insight row for Quick Insights widget
function InsightRow({ icon: Icon, title, value, color }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75,
        borderRadius: tokens.radius.md,
        backgroundColor: tokens.colors.background,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Box
        sx={{
          width: 38, height: 38, borderRadius: tokens.radius.full,
          backgroundColor: `${color}14`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <Icon size={19} color={color} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: tokens.colors.text.muted, fontWeight: 500, display: 'block', mb: 0.25 }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600, color: tokens.colors.text.primary,
            lineHeight: 1.35, wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// Custom tooltip for charts
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const firstData = payload[0]?.payload;
  const title = firstData?.fee_type || firstData?.dateLabel || firstData?.payment_mode || label;

  return (
    <Box
      sx={{
        backgroundColor: tokens.colors.card, p: 1.5,
        borderRadius: tokens.radius.sm, boxShadow: tokens.shadows.md,
        border: `1px solid ${tokens.colors.border}`,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: tokens.colors.text.primary }}>
        {title}
      </Typography>
      {payload.map((item, i) => (
        <Typography key={i} variant="caption" sx={{ display: 'block', color: item.color || tokens.colors.text.secondary }}>
          {item.name}: <strong style={{ color: tokens.colors.text.primary }}>{formatCurrency(item.value)}</strong>
        </Typography>
      ))}
    </Box>
  );
}

// Top label on bar chart columns
const BarLabel = ({ x, y, width, value }) => (
  <text x={x + width / 2} y={y - 6} fill={tokens.colors.text.secondary} textAnchor="middle" fontSize={10} fontWeight={600}>
    {formatCurrency(value)}
  </text>
);

// In-slice percentage label for donut chart
const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);

  if (percent < 0.04) return null;
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// Compact table styling matching StudentDashboardPage
const TABLE_SX = {
  width: '100%', borderCollapse: 'collapse',
  '& th': {
    py: 1.25, px: 1.5, textAlign: 'left', fontSize: '0.75rem', fontWeight: 600,
    color: tokens.colors.text.muted, borderBottom: `1px solid ${tokens.colors.border}`,
    whiteSpace: 'nowrap',
  },
  '& td': {
    py: 1.25, px: 1.5, fontSize: '0.8125rem', color: tokens.colors.text.primary,
    borderBottom: `1px solid ${tokens.colors.border}`, whiteSpace: 'nowrap',
  },
  '& tbody tr:hover': { backgroundColor: tokens.colors.grey[50], cursor: 'pointer' },
  '& tbody tr:last-child td': { borderBottom: 'none' },
};

export function FeeDashboardPage() {
  usePageMeta('Fee Management', [
    { label: 'Fees', path: '/fees' },
    { label: 'Dashboard' },
  ]);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { schoolId } = useSchool();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Filters state
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [datePreset, setDatePreset] = useState('thisMonth');
  const [fromDate, setFromDate] = useState(() => getPresetDates('thisMonth')?.from || '');
  const [toDate, setToDate] = useState(() => getPresetDates('thisMonth')?.to || '');

  const handlePresetChange = (e) => {
    const val = e.target.value;
    setDatePreset(val);
    if (val !== 'custom') {
      const range = getPresetDates(val);
      if (range) {
        setFromDate(range.from);
        setToDate(range.to);
      }
    }
  };

  // Load academic sessions once
  useEffect(() => {
    if (!schoolId) return;
    academicSessionsApi.getAllBySchool(schoolId)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || res?.items || [];
        setSessions(list);
        const active = list.find((s) => s.is_current) || list[0];
        if (active?.id || active?.session_id) {
          setSelectedSessionId(active.id || active.session_id);
        }
      })
      .catch(console.error);
  }, [schoolId]);

  // Fetch dashboard overview data
  const fetchDashboardData = useCallback(async () => {
    if (!schoolId) return;

    if (fromDate && toDate && fromDate > toDate) {
      const errorMsg = 'Collection From date cannot be later than Collection To date.';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const res = await feeService.getFeeDashboardOverview(schoolId, {
        academic_session_id: selectedSessionId || undefined,
        collection_from: fromDate,
        collection_to: toDate,
      });
      setDashboardData(res);
    } catch (err) {
      console.error('Failed to load fee dashboard overview:', err);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load fee dashboard';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedSessionId, fromDate, toDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Extract dashboard data properties safely
  const financialPosition = dashboardData?.financial_position || {};
  const dueSummary = dashboardData?.due_summary || {};
  const collectionPeriod = dashboardData?.collection_period || {};
  const installmentStatusRaw = dashboardData?.installment_status_breakdown || [];
  const paymentModeRaw = dashboardData?.payment_mode_breakdown || [];
  const collectionTrendRaw = dashboardData?.collection_trend || [];
  const feeTypeBreakdown = dashboardData?.fee_type_breakdown || [];
  const topOutstandingStudents = dashboardData?.top_outstanding_students || [];
  const recentReceipts = dashboardData?.recent_receipts || [];

  // Map installment statuses
  const statusMap = useMemo(() => {
    return Object.fromEntries(installmentStatusRaw.map((s) => [s.status, s]));
  }, [installmentStatusRaw]);

  const paidStatus = statusMap.PAID || {};
  const partialStatus = statusMap.PARTIALLY_PAID || {};
  const pendingStatus = statusMap.PENDING || {};

  // Formatted collection trend data
  const collectionTrend = useMemo(() => {
    return collectionTrendRaw.map((item) => ({
      ...item,
      dateLabel: formatDate(item.date),
    }));
  }, [collectionTrendRaw]);

  // Payment mode donut data with total calculation
  const paymentModeData = useMemo(() => {
    const totalAmount = paymentModeRaw.reduce((acc, curr) => acc + (curr.amount || 0), 0) || 1;
    return paymentModeRaw.map((item, idx) => ({
      name: item.payment_mode,
      value: item.amount,
      count: item.transaction_count,
      percentage: +((item.amount / totalAmount) * 100).toFixed(1),
      color: PAYMENT_MODE_COLORS[item.payment_mode] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length],
    }));
  }, [paymentModeRaw]);

  // Quick insights
  const insights = useMemo(() => {
    const topOutstanding = topOutstandingStudents[0];
    const topFeeType = [...feeTypeBreakdown].sort((a, b) => (b.expected || 0) - (a.expected || 0))[0];

    return [
      {
        icon: Award,
        title: 'Top Fee Head',
        value: topFeeType ? `${topFeeType.fee_type} (${formatCurrency(topFeeType.expected)})` : 'None',
        color: '#2563EB',
      },
      {
        icon: ArrowUpRight,
        title: 'Highest Dues',
        value: topOutstanding ? `${topOutstanding.student_name} (${formatCurrency(topOutstanding.outstanding)})` : 'None',
        color: '#DC2626',
      },
      {
        icon: Clock,
        title: 'Overdue Dues',
        value: `${dueSummary.overdue_students || 0} students overdue (${formatCurrency(dueSummary.overdue_amount || 0)})`,
        color: '#F59E0B',
      },
      {
        icon: TrendingUp,
        title: 'Period Collection',
        value: `${formatCurrency(collectionPeriod.collected_amount || 0)} (${collectionPeriod.receipt_count || 0} receipts)`,
        color: '#16A34A',
      },
    ];
  }, [feeTypeBreakdown, topOutstandingStudents, dueSummary, collectionPeriod]);

  const canManage =
    user?.role === ROLES.ORGANIZATION_ADMIN ||
    user?.role === ROLES.PRINCIPAL ||
    user?.role === ROLES.ACCOUNTANT;

  const isInvalidDateRange = Boolean(fromDate && toDate && fromDate > toDate);

  if (loading && !dashboardData) return <Loader message="Loading fee dashboard..." />;

  if (error && !dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader title="Fee Dashboard" subtitle="Overview of student fee collections, outstanding dues, and financial summary" />
        <WidgetContainer sx={{ mt: 3, p: 4 }}>
          <ErrorState
            title="Failed to Load Fee Dashboard"
            message={error}
            onRetry={fetchDashboardData}
          />
        </WidgetContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {/* Page Header & Filters */}
      <PageHeader
        title="Fee Dashboard"
        subtitle="Overview of student fee collections, outstanding dues, and financial summary"
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {sessions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Academic Session</InputLabel>
                <Select
                  value={selectedSessionId}
                  label="Academic Session"
                  onChange={(e) => setSelectedSessionId(e.target.value)}
                >
                  {sessions.map((s) => (
                    <MenuItem key={s.id || s.session_id} value={s.id || s.session_id}>
                      {s.session_name || `${s.start_date} – ${s.end_date}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={datePreset}
                label="Date Range"
                onChange={handlePresetChange}
              >
                {DATE_PRESET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {datePreset === 'custom' && (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="Collection From"
                  value={fromDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFromDate(val);
                    if (toDate && val && val > toDate) {
                      toast.error('Collection From date cannot be later than Collection To date');
                    }
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { max: toDate || undefined },
                  }}
                  error={isInvalidDateRange}
                  helperText={isInvalidDateRange ? 'Must be ≤ Collection To' : undefined}
                  sx={{ width: 145 }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Collection To"
                  value={toDate}
                  onChange={(e) => {
                    const val = e.target.value;
                    setToDate(val);
                    if (fromDate && val && val < fromDate) {
                      toast.error('Collection To date cannot be earlier than Collection From date');
                    }
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: fromDate || undefined },
                  }}
                  error={isInvalidDateRange}
                  helperText={isInvalidDateRange ? 'Must be ≥ Collection From' : undefined}
                  sx={{ width: 145 }}
                />
              </>
            )}
          </Box>
        }
      />

      {/* Error Banner when filter fetch fails */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            borderRadius: tokens.radius.md,
            border: `1px solid ${tokens.colors.error.main}30`,
            backgroundColor: `${tokens.colors.error.main}10`,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Row 1: Primary Financial Position (5 Column Grid) */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
        <DashboardStatCard
          title="Total Expected Fee"
          value={formatCurrency(financialPosition.total_expected)}
          subtext={`${financialPosition.assigned_students || 0} students assigned`}
          subtextColor={tokens.colors.primary.main}
          icon={Wallet}
          color="primary"
          onClick={() => navigate('/fees/assign')}
        />
        <DashboardStatCard
          title="Total Collected"
          value={formatCurrency(financialPosition.total_collected)}
          subtext={`${financialPosition.collection_percentage ?? 0}% collection rate`}
          subtextColor={tokens.colors.success.main}
          icon={CheckCircle}
          color="success"
          onClick={() => navigate('/fees/collect')}
        />
        <DashboardStatCard
          title="Total Outstanding"
          value={formatCurrency(financialPosition.total_outstanding)}
          subtext={`Net Payable: ${formatCurrency(financialPosition.net_payable)}`}
          subtextColor={tokens.colors.error.main}
          icon={AlertCircle}
          color="error"
          onClick={() => navigate('/fees/collect')}
        />
        <DashboardStatCard
          title="Overdue Dues"
          value={formatCurrency(dueSummary.overdue_amount)}
          subtext={`${dueSummary.overdue_students || 0} students (${dueSummary.overdue_installments || 0} inst.)`}
          subtextColor={tokens.colors.warning.main}
          icon={Clock}
          color="warning"
          onClick={() => navigate('/fees/collect')}
        />
        <DashboardStatCard
          title="Period Collection"
          value={formatCurrency(collectionPeriod.collected_amount)}
          subtext={`${collectionPeriod.receipt_count || 0} receipts issued`}
          subtextColor={tokens.colors.info.main}
          icon={TrendingUp}
          color="info"
        />
      </DashboardGrid>

      {/* Row 2: Installment Status & Due Summary (5 Column Grid) */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap={1.75} sx={{ mb: 0 }}>
        <DashboardStatCard
          title="Due Today"
          value={formatCurrency(dueSummary.due_today_amount)}
          subtext={`${dueSummary.due_today_installments || 0} installments`}
          subtextColor={tokens.colors.warning.main}
          icon={AlertCircle}
          color="warning"
        />
        <DashboardStatCard
          title="Paid Installments"
          value={paidStatus.count || 0}
          subtext="Fully paid installments"
          subtextColor={tokens.colors.success.main}
          icon={CheckCircle2}
          color="success"
        />
        <DashboardStatCard
          title="Partially Paid"
          value={partialStatus.count || 0}
          subtext={`${formatCurrency(partialStatus.outstanding || 0)} outstanding`}
          subtextColor={tokens.colors.info.main}
          icon={PieChart}
          color="info"
        />
        <DashboardStatCard
          title="Pending Installments"
          value={pendingStatus.count || 0}
          subtext={`${formatCurrency(pendingStatus.outstanding || 0)} outstanding`}
          subtextColor={tokens.colors.error.main}
          icon={Hourglass}
          color="error"
        />
        <DashboardStatCard
          title="Unassigned Students"
          value={financialPosition.students_without_fee_assignments || 0}
          subtext={`out of ${financialPosition.active_students || 0} active students`}
          subtextColor="#8B5CF6"
          icon={UserMinus}
          color="purple"
          onClick={() => navigate('/fees/assign')}
        />
      </DashboardGrid>

      {/* Row 3: Fee Type Breakdown Bar Chart + Payment Mode Donut Chart */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Fee Breakdown by Head"
          action={
            <Button size="small" variant="outlined" onClick={() => navigate('/fees/types')}>
              View Fee Types
            </Button>
          }
        >
          <Box sx={{ width: '100%', height: 300 }}>
            {feeTypeBreakdown.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={feeTypeBreakdown} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="fee_type" tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false} interval={0}
                    angle={-25} textAnchor="end" height={45}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="expected" name="Expected" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={30} />
                  <Bar dataKey="paid" name="Collected" fill="#16A34A" radius={[4, 4, 0, 0]} maxBarSize={30} />
                  <Bar dataKey="outstanding" name="Outstanding" fill="#DC2626" radius={[4, 4, 0, 0]} maxBarSize={30} />
                  <Legend verticalAlign="top" align="right" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No fee type breakdown available for this selection</Typography>
              </Box>
            )}
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Collections by Payment Mode">
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {paymentModeData.length > 0 ? (
              <ResponsiveContainer>
                <RePieChart>
                  <Pie
                    data={paymentModeData}
                    cx="50%" cy="45%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3} dataKey="value"
                    label={PieLabel} labelLine={false}
                  >
                    {paymentModeData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle" iconSize={10}
                    formatter={(val) => {
                      const item = paymentModeData.find((g) => g.name === val);
                      return (
                        <span style={{ fontSize: 11, color: tokens.colors.text.secondary }}>
                          {val}&nbsp;&nbsp;{formatCurrency(item?.value ?? 0)} ({item?.percentage ?? 0}%)
                        </span>
                      );
                    }}
                  />
                </RePieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No payment mode data available</Typography>
            )}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Row 4: Top Outstanding Students + Collection Trend + Quick Insights */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.45fr 0.9fr 0.65fr' }, gap: 1.75 }}>
        <WidgetContainer
          title="Top Outstanding Students"
          noPadding
          action={
            <Button
              size="small"
              sx={{ textTransform: 'none', color: tokens.colors.primary.main }}
              onClick={() => navigate('/fees/collect')}
            >
              Collect Dues
            </Button>
          }
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={TABLE_SX}>
              <thead>
                <tr>
                  <th style={{ width: 28, textAlign: 'center' }}>#</th>
                  <th>Student Name</th>
                  <th>Adm No.</th>
                  <th>Oldest Due</th>
                  <th style={{ textAlign: 'right' }}>Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {topOutstandingStudents.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: tokens.colors.text.muted, py: 3 }}>
                      No outstanding dues for selected session
                    </td>
                  </tr>
                ) : (
                  topOutstandingStudents.map((stu, idx) => {
                    const studentId = stu.student_id || stu.id || stu._id;
                    return (
                      <tr
                        key={studentId || idx}
                        onClick={() => {
                          if (studentId) {
                            navigate(`/fees/student-status/${studentId}?tab=installments`);
                          } else {
                            navigate('/fees/collect');
                          }
                        }}
                      >
                        <td style={{ width: 28, textAlign: 'center', color: tokens.colors.text.muted, fontSize: '0.75rem' }}>
                          {idx + 1}
                        </td>
                        <td>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 22, height: 22, fontSize: '0.65rem',
                                bgcolor: tokens.colors.primary[100],
                                color: tokens.colors.primary.main,
                                flexShrink: 0,
                              }}
                            >
                              {stu.student_name?.[0]}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {stu.student_name}
                            </Typography>
                          </Box>
                        </td>
                        <td>
                          <Typography variant="caption" sx={{ color: tokens.colors.primary.main, fontWeight: 600 }}>
                            {stu.admission_no}
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="caption" sx={{ color: tokens.colors.text.secondary, whiteSpace: 'nowrap' }}>
                            {formatDate(stu.oldest_due_date)}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.colors.error.main, whiteSpace: 'nowrap' }}>
                            {formatCurrency(stu.outstanding)}
                          </Typography>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Daily Collection Trend">
          <Box sx={{ width: '100%', height: 250 }}>
            {collectionTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={collectionTrend} margin={{ top: 10, right: 10, left: -15, bottom: 5 }}>
                  <defs>
                    <linearGradient id="feeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.colors.border} />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false} interval={0}
                    angle={-25} textAnchor="end" height={40}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: tokens.colors.text.muted }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone" dataKey="amount" name="Collection Amount"
                    stroke={CHART_COLOR} fill="url(#feeGrad)" strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No collection trend available</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${tokens.colors.border}` }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Discounts</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.colors.warning.main }}>
                {formatCurrency(collectionPeriod.discount_amount)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Fines Collected</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.colors.error.main }}>
                {formatCurrency(collectionPeriod.fine_amount)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: tokens.colors.text.muted }}>Avg Receipt</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.colors.primary.main }}>
                {formatCurrency(collectionPeriod.average_receipt_amount)}
              </Typography>
            </Box>
          </Box>
        </WidgetContainer>

        <WidgetContainer title="Quick Insights">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {insights.map((item, i) => (
              <InsightRow key={i} {...item} />
            ))}
          </Box>
        </WidgetContainer>
      </Box>

      {/* Row 5: Recent Receipts & Quick Actions */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: canManage ? '1fr 1fr' : '1fr' }, gap: 3 }}>
        <WidgetContainer title="Recent Receipts" noPadding>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={TABLE_SX}>
              <thead>
                <tr>
                  <th style={{ width: 28, textAlign: 'center' }}>#</th>
                  <th>Receipt No.</th>
                  <th>Student Name</th>
                  <th>Date</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentReceipts.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: tokens.colors.text.muted, py: 3 }}>
                      No recent receipts issued
                    </td>
                  </tr>
                ) : (
                  recentReceipts.map((rcpt, idx) => {
                    const studentId = rcpt.student_id || rcpt.studentId || rcpt.student?.id;
                    return (
                      <tr
                        key={rcpt.receipt_id || rcpt.id || idx}
                        onClick={() => {
                          if (studentId) {
                            navigate(`/fees/student-status/${studentId}?tab=payment-history`);
                          } else {
                            navigate('/fees/collect');
                          }
                        }}
                      >
                        <td style={{ width: 28, textAlign: 'center', color: tokens.colors.text.muted, fontSize: '0.75rem' }}>
                          {idx + 1}
                        </td>
                        <td>
                          <Typography variant="caption" sx={{ color: tokens.colors.primary.main, fontWeight: 600 }}>
                            {rcpt.receipt_no}
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {rcpt.student_name} ({rcpt.admission_no})
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="caption" sx={{ color: tokens.colors.text.secondary, whiteSpace: 'nowrap' }}>
                            {formatDate(rcpt.receipt_date)}
                          </Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.colors.success.main, whiteSpace: 'nowrap' }}>
                            {formatCurrency(rcpt.amount)}
                          </Typography>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Box>
          </Box>
        </WidgetContainer>

        {canManage && (
          <WidgetContainer title="Quick Actions">
            <Box sx={{ p: 1 }}>
              <DashboardGrid columns={{ xs: 1, sm: 2 }} gap={1.5}>
                <QuickActionCard title="Collect Student Fee" icon={Wallet} path="/fees/collect" color="success" />
                <QuickActionCard title="Assign Fees to Students" icon={Users} path="/fees/assign" color="primary" />
                <QuickActionCard title="Fee Structures" icon={Layers} path="/fees/structures" color="warning" />
                <QuickActionCard title="Fee Installments" icon={Clock} path="/fees/installments" color="secondary" />
              </DashboardGrid>
            </Box>
          </WidgetContainer>
        )}
      </Box>
    </Box>
  );
}
